// Posten in Schichten (B25, Fabi 29.10.2026; Erste Fassung): Käpt'n, Ausguck, Gemeinschaftsraum, Heiler; wer keinen
// Posten hat, ruht in der Kajüte. Eine Schicht ist die Strecke zwischen zwei Halten; zuteilen kann man nur zwischen den
// Schichten, also wenn das Boot nach einem Halt (oder am Morgen) wieder ablegt. Jede Spezies ist auf jedem Posten gut,
// normal oder schlecht (S12). Rein und deterministisch wie der Rest des Runs.
import { getSpecies, POST_IDS, runRules, type Aptitude, type PostId } from '../../content/index.ts';
import type { Posts, RunFriend, RunState } from './types.ts';
import { maxHp } from './profile.ts';

export type PostsView = Posts & { kajuete: string[] };

/** S12: wie gut diese Spezies auf dem Posten ist */
export const aptitude = (species: string, post: PostId): Aptitude => getSpecies(species).aboard?.[post] ?? 'normal';

const RANK: Record<Aptitude, number> = { gut: 2, normal: 1, schlecht: 0 };

/** So viele passen in den Gemeinschaftsraum, je nach Ausbaustufe des Boots [ZAHL?] */
export function commonPlaces(run: Pick<RunState, 'crewSlots'>): number {
  const stage = Math.max(0, run.crewSlots - runRules.crewSlots.start);
  const list = runRules.posts.gemeinschaftPlaces;
  return list[Math.min(list.length - 1, stage)] ?? 2;
}

const onBoard = (run: RunState) => run.crew.filter((f) => !f.away);

/**
 * Vorschlag, solange niemand zugeteilt hat: Käpt'n und Ausguck nach Eignung, bei Gleichstand wer gesünder ist; der Heiler,
 * wenn jemand verletzt ist und einer gut darin; in den Gemeinschaftsraum zwei, die es gut können, wenn genug an Bord sind
 */
export function defaultPosts(run: RunState): Posts {
  const crew = onBoard(run);
  const used = new Set<string>();
  const share = (f: RunFriend) => f.hp / Math.max(1, maxHp(f, run));
  const best = (post: PostId, filter: (f: RunFriend) => boolean = () => true) =>
    crew
      .filter((f) => !used.has(f.id) && filter(f))
      .sort((a, b) => RANK[aptitude(b.species, post)] - RANK[aptitude(a.species, post)] || share(b) - share(a) || a.id.localeCompare(b.id))[0];
  const posts: Posts = { kaeptn: null, ausguck: null, gemeinschaft: [], heiler: null };
  const k = best('kaeptn');
  if (k) {
    posts.kaeptn = k.id;
    used.add(k.id);
  }
  const a = best('ausguck');
  if (a && crew.length > 1) {
    posts.ausguck = a.id;
    used.add(a.id);
  }
  return posts;
}

/** Die Posten, wie sie gerade gelten: gespeichert oder vorgeschlagen; wer fehlt oder unterwegs ist, fällt heraus */
export function postsOf(run: RunState): PostsView {
  const here = new Set(onBoard(run).map((f) => f.id));
  const raw = run.posts ?? defaultPosts(run);
  const ok = (id: string | null) => (id && here.has(id) ? id : null);
  const seen = new Set<string>();
  const take = (id: string | null) => {
    const v = ok(id);
    if (!v || seen.has(v)) return null;
    seen.add(v);
    return v;
  };
  const posts: Posts = { kaeptn: take(raw.kaeptn), ausguck: take(raw.ausguck), heiler: take(raw.heiler), gemeinschaft: [] };
  for (const id of raw.gemeinschaft) {
    const v = take(id);
    if (v && posts.gemeinschaft.length < commonPlaces(run)) posts.gemeinschaft.push(v);
  }
  const kajuete = onBoard(run).filter((f) => !seen.has(f.id)).map((f) => f.id);
  return { ...posts, kajuete };
}

/** Wo dieser Freund gerade ist: ein Posten, die Kajüte oder unterwegs */
export function placeOf(run: RunState, id: string): PostId | 'kajuete' | 'unterwegs' {
  const f = run.crew.find((x) => x.id === id);
  if (!f || f.away) return 'unterwegs';
  const p = postsOf(run);
  for (const post of POST_IDS) {
    const v = p[post];
    if (v === id || (Array.isArray(v) && v.includes(id))) return post;
  }
  return 'kajuete';
}

/** B25: Zuteilen geht nur zwischen den Schichten, also bevor das Boot nach einem Halt oder am Morgen ablegt */
export const canAssign = (run: RunState): boolean => run.pending.kind === 'river' && !(run.shift?.underway ?? false);

/**
 * B25: Einen Freund auf einen Posten setzen (oder mit null in die Kajüte). Wer den Posten vorher hatte, geht in die
 * Kajüte; im Gemeinschaftsraum rückt, wer zuerst kam, heraus, wenn er voll ist.
 */
export function assignPost(run: RunState, id: string, post: PostId | 'kajuete'): void {
  if (!canAssign(run)) throw new Error('Zuteilen geht nur zwischen den Schichten');
  const f = run.crew.find((x) => x.id === id);
  if (!f || f.away) throw new Error('Dieser Freund ist nicht an Bord');
  const cur = postsOf(run);
  const next: Posts = { kaeptn: cur.kaeptn, ausguck: cur.ausguck, heiler: cur.heiler, gemeinschaft: cur.gemeinschaft.filter((x) => x !== id) };
  for (const p of ['kaeptn', 'ausguck', 'heiler'] as const) if (next[p] === id) next[p] = null;
  if (post === 'gemeinschaft') {
    next.gemeinschaft.push(id);
    while (next.gemeinschaft.length > commonPlaces(run)) next.gemeinschaft.shift();
  } else if (post !== 'kajuete') next[post] = id;
  run.posts = next;
}

/** B25: Die Schicht beginnt, sobald das Boot ablegt; die Zuteilung steht bis zum nächsten Halt fest */
export function startShift(run: RunState): void {
  const p = postsOf(run);
  run.posts = { kaeptn: p.kaeptn, ausguck: p.ausguck, heiler: p.heiler, gemeinschaft: [...p.gemeinschaft] };
  run.shift = { underway: true, gemeinschaft: [...p.gemeinschaft] };
}

/** B25: Die Schicht endet mit einem Halt; wer sie zusammen im Gemeinschaftsraum war, ist jetzt verbunden */
export function endShift(run: RunState): void {
  const together = run.shift?.underway ? run.shift.gemeinschaft.filter((id) => run.crew.some((f) => f.id === id && !f.away)) : [];
  if (together.length >= 2) run.bonds = [together];
  run.shift = { underway: false, gemeinschaft: [] };
}
